import { useMemo, useState } from "react";
import { ReportShell } from "../ReportShell";
import { FilterChips, type Chip } from "@/components/FilterChips";
import { BasisToggle, CompetitionPage, MarketPositionPage } from "./CeoPages";
import { useCeoAtoms } from "./data";
import type { Basis } from "./pool";
import { loadActiveProfile } from "@/lib/auth";
import { DEFAULT_RANGE, type DateRange } from "@/lib/dateRange";
import type { Route } from "@/lib/route";

const TITLES: Record<string, string> = {
  "market-position": "Market position",
  competition: "Competition",
};

/**
 * CEO View. Sub-pages mirror the published report's own rail, in its order.
 *
 * BASIS LIVES HERE, not in a sub-page. Every rupee on this view carries a basis
 * — MRP is what plan and target are set on, SP is what was realised after
 * discount — and that is only a true statement if all three sub-pages are on
 * the same one. Putting the toggle in the shared filter row is what makes it
 * impossible to read a Competition table on SP beside a Portfolio table on MRP.
 */
export function CeoView({
  route,
  onNavigate,
  onSignOut,
}: {
  route: Route;
  onNavigate: (section: string, page: string) => void;
  onSignOut?: () => void;
}) {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [basis, setBasis] = useState<Basis>("mrp");
  const customer = loadActiveProfile() ?? "";
  const page = route.page || "market-position";

  /**
   * The window the API resolved, so the date pill shows real dates rather than
   * an em-dash. Read from the same atoms query the pages read — identical
   * arguments mean an identical query key, so this costs nothing extra.
   */
  const anchor = useCeoAtoms(customer, range, category);

  // The atoms endpoint resolves the category via universe lookup (e.g.
  // O'cean → "Hydration Drinks" from tenant_category_universe). The L2
  // ("Hydration Drinks") is what the SQL filters on; the universe name
  // ("RTD Hydration") is the display label for the chip.
  const resolvedL2 = anchor.cur?.category ?? category;
  const displayCategory =
    anchor.cur?.universe_name ?? anchor.cur?.category ?? category;

  const chips: Chip[] = useMemo(
    () => [
      {
        kind: "category",
        label: displayCategory ?? "All Categories",
        value: resolvedL2,
      },
    ],
    [displayCategory, resolvedL2],
  );

  const props = { customer, category: resolvedL2, range, basis };

  return (
    <ReportShell
      title={`CEO View — ${TITLES[page] ?? "Market position"}`}
      route={{ section: "ceo", page }}
      onNavigate={onNavigate}
      range={range}
      onRangeChange={setRange}
      {...(anchor.win ? { resolved: anchor.win } : {})}
      {...(onSignOut ? { onSignOut } : {})}
      filters={
        // The toggle is placed INSIDE the chip strip rather than beside it: the
        // strip is a full-width band with its own bottom border, and putting a
        // second control next to it would leave the band short of the page edge.
        <div style={{ position: "relative" }}>
          <FilterChips
            chips={chips}
            customerName={customer}
            onSelect={(_, value) => {
              setCategory(value);
            }}
            onClear={() => {
              setCategory(undefined);
            }}
          />
          <div
            style={{
              position: "absolute",
              right: 12,
              top: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <BasisToggle basis={basis} onChange={setBasis} />
          </div>
        </div>
      }
    >
      {page === "competition" && <CompetitionPage {...props} />}
      {page !== "competition" && <MarketPositionPage {...props} />}
    </ReportShell>
  );
}
